import { useAuthenticatedFetch } from './apiService';
import { camelToSnakeObject } from '../utils/camelToSnake';
import { snakeToCamelObject } from '../utils/snakeToCamel';
import { useMemo } from 'react';

export interface AssessmentType {
  id: string;
  assignmentId?: number;
  title: string;
  description: string;
  duration: string;
  meetingSummary: string;
  goals: string[];
  promptTemplateId? : number;
  courseId?: number;
  active?: boolean;
  published: boolean;
  personaName: string;
  personaRole: string;
  personaBehaviors: string;
  personaBio: string;
  voiceName: string;
  voiceGender: string;
  companyName: string;
  companyIndustry: string;
  companySize: string;
  companyLocations: string;
  companyTechProfile: string;
  companyChallenges: string;
  scenarioMeetingType: string;
  scenarioCustomerType: string;
  scenarioHistory: string;
  scenarioInitiatives: string;
  scenarioChallenges: string;
  scenarioConstraints: string; 
  createdAt: string;
  customer: string;
  status: string;
  startDate: Date;
  endDate: Date;
}

export interface AssignedAssessmentType extends AssessmentType {
  assignmentId: number;
}

export const useAssessmentService = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMemo(() => ({
    createAssessment: async (assessment: Partial<AssessmentType>): Promise<AssessmentType> => {
      const snakeAssessment = camelToSnakeObject(assessment);
      const res = await authenticatedFetch('/scenario', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(snakeAssessment),
      });
      if (!res.ok) throw new Error('Failed to create assessment');
      return snakeToCamelObject(await res.json());
    },

    fetchAssessments: async ( filters?: Record <string, string> ): Promise<AssignedAssessmentType[]> => {
      let url = ''
      if (filters) {
        const filteredFilters = Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== '' && v !== undefined && v !== null)
        );
        const params = new URLSearchParams(filteredFilters).toString();
        url = `/scenario/assigned/?${params}`
      }
      else {
        url = `/scenario/assigned/`
      }
      const res = await authenticatedFetch(url);
      if (!res.ok) throw new Error('Failed to load assigned assessments');
      const assessments = snakeToCamelObject(await res.json()) as AssignedAssessmentType[];
      for (const assessment of assessments) {
        if (!Number.isFinite(assessment.assignmentId) || Number(assessment.assignmentId) <= 0) {
          throw new Error('Assigned assessment is missing a valid assignment id');
        }
      }
      return assessments;
    },

    fetchAssessment: async (id: string): Promise<AssessmentType> => {
      const res = await authenticatedFetch(`/scenario/${id}`);
      if (!res.ok) throw new Error('Assessment not found');
      return snakeToCamelObject(await res.json());
    },

    fetchAllAssessments: async (): Promise<AssessmentType[]> => {
      const res = await authenticatedFetch('/scenario/all');
      if (!res.ok) throw new Error('Failed to gather assessments');
      return snakeToCamelObject(await res.json());
    },

    fetchAllAssessmentNames: async (): Promise<[]> => {
      const res = await authenticatedFetch('/scenario/all_names');
      if (!res.ok) throw new Error('Failed to gather assessments');
      return snakeToCamelObject(await res.json());
    },

    quickAssign: async (assessmentId: string): Promise<AssignedAssessmentType> => {
      const res = await authenticatedFetch(`/scenario/${assessmentId}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('Failed to quick assign assessment');
      const assignment = snakeToCamelObject(await res.json()) as AssignedAssessmentType;
      if (!Number.isFinite(assignment.assignmentId) || Number(assignment.assignmentId) <= 0) {
        throw new Error('Quick assign did not return a valid assignment id');
      }
      return assignment;
    },

    updateAssessment: async (assessment: AssessmentType): Promise<AssessmentType> => {
      const snakeAssessment = camelToSnakeObject(assessment);
      const res = await authenticatedFetch(`/scenario/${assessment.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(snakeAssessment),
      });
      if (!res.ok) throw new Error('Failed to update assessment');
      return snakeToCamelObject(await res.json());
    },
  }), [authenticatedFetch]);
};